// ABOUTME: Staff create a client company from its name and website, then land on its page.
// ABOUTME: A website whose domain already has a company links to that company instead of creating another.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Button from '$components/Buttons/Button';
import BasicInput from '$components/Inputs/BaseInput';
import FormFooter from '$components/FormFooter/FormFooter';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { SIMERA_COMPANY_ROUTE } from '$constants/routes';
import { useCreateCompanyMutation } from '$store/modules/companies';
import type { CreateCompanyError } from '$store/modules/companies';
import { SaveState } from '$types/ForrmType';

interface FormData {
  Name: string;
  Url: string;
}

const NewClient = () => {
  const [t, i18next] = useTranslation('global');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [existingCompany, setExistingCompany] =
    useState<CreateCompanyError['Company']>();
  const [createCompany, { isLoading }] = useCreateCompanyMutation();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { Name: '', Url: '' } });

  const name = register('Name', {
    required: `${t('CompaniesTable.ErrorName')}`,
    setValueAs: (value: string) => value.trim(),
  });
  const url = register('Url', {
    required: `${t('CompaniesTable.ErrorWebsite')}`,
    setValueAs: (value: string) => value.trim(),
  });

  const closeModal = () => {
    setIsOpen(false);
    setExistingCompany(undefined);
    reset();
  };

  const onSubmit = async (data: FormData) => {
    setExistingCompany(undefined);
    const response = await createCompany({
      ...data,
      lang: i18next.language as 'en' | 'es',
    });
    if ('data' in response && response.data) {
      navigate(SIMERA_COMPANY_ROUTE(response.data.Id));
      return;
    }
    const error = ('error' in response ? response.error : undefined) as
      CreateCompanyError | undefined;
    if (error?.statusCode === 409 && error.Company) {
      setExistingCompany(error.Company);
    } else if (error?.statusCode === 400) {
      const { message } = error;
      setError('Url', {
        message: Array.isArray(message) ? message.join('. ') : message,
      });
    }
  };

  return (
    <>
      <Button
        text={t('CompaniesTable.NewClient')}
        showText
        bgColor="#079e88"
        textStyles={{ color: 'white' }}
        styling="px-4"
        size="auto"
        onClick={() => setIsOpen(true)}
      />
      <ModalInTailwind isOpen={isOpen} closeModal={closeModal}>
        <form
          className="bg-white rounded p-8 flex flex-col w-full max-w-lg"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h2 className="font-bold text-xl mb-4">
            {t('CompaniesTable.NewClient')}
          </h2>
          <BasicInput
            type="text"
            label={t('CompaniesTable.Name')}
            isRequired
            name={name.name}
            refProp={name.ref}
            onChange={name.onChange}
            onBlur={name.onBlur}
            errorText={errors.Name?.message ?? ''}
            dataTestId="new-client-name"
          />
          <BasicInput
            type="text"
            label={t('CompaniesTable.Website')}
            isRequired
            name={url.name}
            refProp={url.ref}
            onChange={url.onChange}
            onBlur={url.onBlur}
            errorText={errors.Url?.message ?? ''}
            dataTestId="new-client-website"
          />
          {existingCompany && (
            <p className="mt-4">
              {t('CompaniesTable.CompanyExists')}{' '}
              <Link
                className="underline"
                to={SIMERA_COMPANY_ROUTE(existingCompany.Id)}
              >
                {existingCompany.Deleted
                  ? `${existingCompany.Name} ${t('CompaniesTable.Deleted')}`
                  : existingCompany.Name}
              </Link>
            </p>
          )}
          <div className="mt-4">
            <FormFooter
              translate={t}
              saveState={isLoading ? SaveState.PENDING : SaveState.IDLE}
              submit
              onClickCancel={closeModal}
              disabled={isLoading}
            />
          </div>
        </form>
      </ModalInTailwind>
    </>
  );
};

export default NewClient;
